import React, { useState, useMemo } from "react";
import { ArrowLeft, MapPin, Flame, Search, X, TrendingUp, Building2 } from "lucide-react";
import { ExhibitionCountry, ExhibitionState } from "@/hooks/useExhibition";
import { LiveBadge } from "@/components/live-arenas/LiveBadge";
import BackgroundBalloons from "@/components/pop-balloon/BackgroundBalloons";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface ExhibitionStateScreenProps {
  country: ExhibitionCountry;
  states: ExhibitionState[];
  loading: boolean;
  remainingRequests: number;
  onBack: () => void;
  onSelectState: (stateId: string) => void;
}

const ExhibitionStateScreen: React.FC<ExhibitionStateScreenProps> = ({
  country,
  states,
  loading,
  remainingRequests,
  onBack,
  onSelectState,
}) => {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter states based on search
  const filteredStates = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return states;
    return states.filter(state => state.name.toLowerCase().includes(query));
  }, [states, searchQuery]);

  // Get trending states (sorted by activity)
  const trendingStates = useMemo(() => {
    return [...filteredStates]
      .filter(state => state.participantCount > 0 || state.boothCount > 0)
      .sort((a, b) => (b.participantCount * 2 + b.boothCount) - (a.participantCount * 2 + a.boothCount))
      .slice(0, 4);
  }, [filteredStates]);

  // Get remaining states (excluding trending when not searching)
  const remainingStates = useMemo(() => {
    if (searchQuery) return filteredStates;
    const trendingIds = new Set(trendingStates.map(s => s.id));
    return filteredStates.filter(state => !trendingIds.has(state.id));
  }, [filteredStates, trendingStates, searchQuery]);

  const totalBooths = states.reduce((sum, s) => sum + s.boothCount, 0);

  return (
    <div className="min-h-screen sky-gradient relative overflow-hidden">
      <BackgroundBalloons />

      <div className="relative z-10 flex flex-col min-h-screen pb-20">
        {/* Header */}
        <div className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <button onClick={onBack} className="p-2 -ml-2">
              <ArrowLeft className="w-6 h-6 text-foreground" />
            </button>
            <LiveBadge variant="glow" />
          </div>

          <div className="text-center mt-2">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-2xl">{country.flag}</span>
              <h1 className="text-3xl font-bold text-foreground">{country.name}</h1>
            </div>
            <p className="text-muted-foreground text-sm">Select a state</p>
          </div>
        </div>

        {/* Country stats */}
        <div className="mx-4 mt-4 p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-md">
          <div className="flex items-center justify-around">
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{states.length}</p>
              <p className="text-xs text-muted-foreground">States</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <p className="text-2xl font-bold text-pop">{totalBooths}</p>
              <p className="text-xs text-muted-foreground">Live Stages</p>
            </div>
            <div className="w-px h-8 bg-border" />
            <div className="text-center">
              <p className="text-lg font-bold text-muted-foreground">{remainingRequests}/5</p>
              <p className="text-xs text-muted-foreground">Requests Left</p>
            </div>
          </div>
        </div>

        {/* Search Input */}
        {states.length > 5 && (
          <div className="mx-4 mt-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search states..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9 h-11 rounded-xl bg-white/60 backdrop-blur-sm border-white/50 focus-visible:ring-1 focus-visible:ring-primary"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* States grid */}
        <div className="flex-1 overflow-y-auto px-4 mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-10 h-10 rounded-full border-4 border-pop border-t-transparent animate-spin" />
            </div>
          ) : filteredStates.length === 0 && searchQuery ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No states found matching "{searchQuery}"</p>
              <button 
                onClick={() => setSearchQuery("")}
                className="mt-2 text-sm text-primary hover:underline"
              >
                Clear search
              </button>
            </div>
          ) : states.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No states available</p>
            </div>
          ) : (
            <div className="space-y-4 pb-8">
              {/* Trending States */}
              {trendingStates.length > 0 && !searchQuery && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-white">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm font-bold">Trending</span>
                      <Flame className="w-3 h-3 animate-pulse" />
                    </div>
                    <span className="text-xs text-muted-foreground">Most active</span>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    {trendingStates.map((state, index) => (
                      <StateCard 
                        key={`trending-${state.id}`} 
                        state={state} 
                        onClick={() => onSelectState(state.id)}
                        showRank={index + 1}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* All/Remaining States */}
              {remainingStates.length > 0 && (
                <div className="space-y-3">
                  {trendingStates.length > 0 && !searchQuery && (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">All States</span>
                      <span className="text-xs text-muted-foreground">({remainingStates.length})</span>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-2 gap-3">
                    {remainingStates.map((state) => (
                      <StateCard key={state.id} state={state} onClick={() => onSelectState(state.id)} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface StateCardProps {
  state: ExhibitionState;
  onClick: () => void;
  showRank?: number;
}

const StateCard: React.FC<StateCardProps> = ({ state, onClick, showRank }) => {
  const isActive = state.participantCount > 0;
  const isHot = state.isHot;

  return (
    <button
      onClick={onClick}
      className={cn(
        "relative p-4 rounded-2xl backdrop-blur-xl bg-white/50 border border-white/60 shadow-lg transition-all duration-300 hover:scale-[1.03] hover:shadow-xl text-left overflow-hidden",
        isActive && "ring-2 ring-pop/30",
        isHot && "ring-2 ring-amber-400/50"
      )}
    >
      {/* Rank badge */}
      {showRank && (
        <div className={cn(
          "absolute top-2 left-2 w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold shadow-lg",
          showRank === 1 ? "bg-amber-500 text-white" :
          showRank === 2 ? "bg-gray-400 text-white" :
          showRank === 3 ? "bg-amber-700 text-white" :
          "bg-white/90 text-foreground"
        )}>
          {showRank}
        </div>
      )}

      {/* Hot badge */}
      {isHot && !showRank && (
        <div className="absolute top-2 right-2">
          <span className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-medium">
            <Flame className="w-2.5 h-2.5" />
            HOT
          </span>
        </div>
      )}

      {/* State icon */}
      <div className={cn("mb-3", showRank && "ml-6")}>
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
      </div>

      <h3 className="font-bold text-foreground truncate">{state.name}</h3>

      <div className="flex items-center gap-2 mt-1 text-sm">
        <span className="text-muted-foreground">{state.cityCount} cities</span>
        {isActive && (
          <span className="flex items-center gap-1 text-green-600 font-medium">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
            {state.participantCount}
          </span>
        )}
      </div>

      <div className="mt-1 text-xs text-muted-foreground">
        {state.boothCount} stages
      </div>
    </button>
  );
};

export default ExhibitionStateScreen;
